import { createStore } from 'zustand/vanilla';
import { immer } from 'zustand/middleware/immer';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useStore } from 'zustand';
import { createContext, useContext } from 'react';
import { safeLocalStorage } from '@ev-tech/search-modules-shared/storage';
import type { ChatbotRequestContext } from '@ev-tech/search-modules-api';

import { conversationSchema } from '../utils/conversation';
import type { ConversationMessage, PageSkill, SkillStatus } from '../types';

type ChatbotState = {
  conversation: ConversationMessage[];
  retryMessageId: string | null;
  open: boolean;
  expanded: boolean;
  draft: string;
  pageSkills: PageSkill[];
  context: ChatbotRequestContext;
  setConversation: (conversation: ConversationMessage[], retryMessageId?: string | null) => void;
  setOpen: (open: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  setDraft: (draft: string) => void;
  setSkillStatus: (messageId: string, status: SkillStatus) => void;
  registerPageSkill: (skill: PageSkill) => () => void;
  registerContext: (context: ChatbotRequestContext) => () => void;
  clearConversation: () => void;
};

const expireConfirmations = (state: ChatbotState, type: string) => {
  for (const message of state.conversation) {
    if (message.skillName === type && message.skillStatus === 'awaiting-confirmation') message.skillStatus = 'unavailable';
  }
};

export const createChatbotStore = (conversationKey: string, storage = safeLocalStorage) =>
  createStore<ChatbotState>()(
    persist(
      immer((set, get) => ({
        conversation: [],
        retryMessageId: null,
        open: false,
        expanded: false,
        draft: '',
        pageSkills: [],
        context: {},

        setConversation: (conversation, retryMessageId = null) => set({ conversation, retryMessageId }),

        setOpen: (open) =>
          set((state) => {
            state.open = open;

            if (!open) state.expanded = false;
          }),

        setExpanded: (expanded) => set({ expanded }),

        setDraft: (draft) => set({ draft }),

        setSkillStatus: (messageId, status) =>
          set((state) => {
            const message = state.conversation.find((entry) => entry.id === messageId);

            if (message) message.skillStatus = status;
          }),

        clearConversation: () => set({ conversation: [], retryMessageId: null, draft: '' }),

        registerPageSkill: (skill) => {
          if (get().pageSkills.some((registered) => registered.type === skill.type)) {
            console.warn(`Chatbot: page skill "${skill.type}" is already registered. Ignoring the duplicate.`);

            return () => {};
          }

          set((state) => {
            expireConfirmations(state, skill.type);
            state.pageSkills.push(skill);
          });

          return () =>
            set((state) => {
              expireConfirmations(state, skill.type);
              state.pageSkills = state.pageSkills.filter((registered) => registered.type !== skill.type);
            });
        },

        registerContext: (context) => {
          const entries = Object.entries(context).filter(([key]) => {
            if (Object.hasOwn(get().context, key)) {
              console.warn(`Chatbot: context key "${key}" is already registered. Ignoring the duplicate.`);

              return false;
            }

            return true;
          });

          set((state) => {
            for (const [key, value] of entries) state.context[key] = value;
          });

          return () =>
            set((state) => {
              for (const [key] of entries) delete state.context[key];
            });
        },
      })),
      {
        name: `ev-chatbot:${conversationKey}`,
        version: 1,
        storage: createJSONStorage(() => storage),
        skipHydration: typeof window === 'undefined',

        partialize: ({ conversation, retryMessageId }) => ({ conversation, retryMessageId }),

        merge: (persisted, current) => {
          const result = conversationSchema.safeParse(persisted);

          if (!result.success) return current;

          return {
            ...current,
            ...result.data,
            conversation: result.data.conversation.map((message) => ({
              ...message,
              ...(message.skillStatus === 'executing' && { skillStatus: 'failed' as const }),
              ...(message.skillStatus === 'awaiting-confirmation' && { skillStatus: 'unavailable' as const }),
            })),
          };
        },
      },
    ),
  );

export type ChatbotStore = ReturnType<typeof createChatbotStore>;

export const ChatbotStoreContext = createContext<ChatbotStore | null>(null);

export const useChatbotStoreApi = () => {
  const store = useContext(ChatbotStoreContext);

  if (!store) throw new Error('Chatbot components and hooks must be inside ChatbotProvider.');

  return store;
};

export const useChatbotStore = <T,>(selector: (state: ChatbotState) => T): T => useStore(useChatbotStoreApi(), selector);
